import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useDialog } from '../../context/DialogContext';
import {
  useMealOptions,
  useCreateMealOption,
  useDeleteMealOption,
  useUpdateMealOption,
} from '../../hooks/useInvites';

/**
 * Per-event meal options manager (seeded server-side from the admin
 * `meal_options` setting list). Embedded in the campaign modal.
 */
export function MealOptionsSection({ eventId }: { eventId: string }) {
  const { confirm } = useDialog();
  const { data: options = [] } = useMealOptions(eventId);
  const createOption = useCreateMealOption();
  const updateOption = useUpdateMealOption();
  const deleteOption = useDeleteMealOption();

  const [name, setName] = useState('');
  const [isKidsMeal, setIsKidsMeal] = useState(false);

  const handleAdd = async () => {
    if (!name.trim()) return;
    await createOption.mutateAsync({
      eventId,
      input: { name: name.trim(), isKidsMeal, sortOrder: options.length },
    });
    setName('');
    setIsKidsMeal(false);
  };

  const handleDelete = async (optionId: string, optionName: string) => {
    const ok = await confirm({
      message: `Delete meal option "${optionName}"? Members with this choice keep no meal.`,
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await deleteOption.mutateAsync({ eventId, optionId });
  };

  return (
    <div className="space-y-2" data-testid="meal-options-section">
      <h3 className="text-sm font-semibold text-gray-900">Meal Options</h3>
      {options.length === 0 && (
        <p className="text-xs text-gray-500">
          No meal options — guests won&apos;t be asked for meal choices.
        </p>
      )}
      <ul className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <li
            key={option.id}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${
              option.isActive
                ? 'border-gray-300 text-gray-800'
                : 'border-gray-200 text-gray-400 line-through'
            }`}
          >
            <button
              type="button"
              title={option.isActive ? 'Deactivate' : 'Activate'}
              onClick={() =>
                void updateOption.mutateAsync({
                  eventId,
                  optionId: option.id,
                  updates: { isActive: !option.isActive },
                })
              }
              className="hover:text-primary-600"
            >
              {option.name}
              {option.isKidsMeal ? ' (kids)' : ''}
            </button>
            <button
              type="button"
              aria-label={`Delete meal option ${option.name}`}
              onClick={() => void handleDelete(option.id, option.name)}
              className="text-gray-400 hover:text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New meal option…"
          aria-label="New meal option name"
          className="flex-1 min-w-40 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        />
        <label className="inline-flex items-center gap-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isKidsMeal}
            onChange={(e) => setIsKidsMeal(e.target.checked)}
          />
          Kids meal
        </label>
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={!name.trim()}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>
    </div>
  );
}
